import React, { useState, useEffect } from "react";
import { api, eur } from "../lib/api";
import { useConfig } from "../context/ConfigContext";
import { enqueue } from "../lib/offline";
import { Plus, Trash2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const emptyLine = () => ({ product: "", quantity: 1, unit: "pz", price: 0, category_id: null });

export function newInvoice() {
  return {
    supplier_id: null,
    supplier_name: "",
    date: new Date().toISOString().slice(0, 10),
    currency: "EUR",
    exchange_rate: 1,
    is_credit_note: false,
    photo_file_id: null,
    line_items: [emptyLine()],
    notes: "",
  };
}

export default function InvoiceForm({ initial, onClose, onSaved }) {
  const { config } = useConfig();
  const [inv, setInv] = useState(initial || newInvoice());
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);
  const cats = config?.categories || [];

  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {}); }, []);

  const setLine = (i, patch) => {
    const lines = inv.line_items.map((l, idx) => (idx === i ? { ...l, ...patch } : l));
    setInv({ ...inv, line_items: lines });
  };
  const addLine = () => setInv({ ...inv, line_items: [...inv.line_items, emptyLine()] });
  const removeLine = (i) => setInv({ ...inv, line_items: inv.line_items.filter((_, idx) => idx !== i) });

  const subtotal = inv.line_items.reduce((s, l) => s + Number(l.price || 0), 0);
  const totalEur = subtotal * Number(inv.exchange_rate || 1) * (inv.is_credit_note ? -1 : 1);

  const save = async () => {
    if (!inv.supplier_name.trim()) return toast.error("Indica il fornitore");
    setSaving(true);
    const payload = { ...inv };
    payload.line_items = payload.line_items.map((l) => {
      const c = cats.find((x) => x.id === l.category_id);
      return { ...l, quantity: Number(l.quantity), price: Number(l.price), category_name: c?.name, is_food: c ? c.is_food : true };
    });
    try {
      if (!navigator.onLine) {
        await enqueue("invoice", payload);
        toast.success("Salvato offline — sincronizzerà al ritorno del segnale");
        onSaved && onSaved();
        onClose();
        return;
      }
      await api.post("/invoices", payload);
      toast.success("Fattura salvata");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      await enqueue("invoice", payload);
      toast.warning("Rete assente: salvato offline");
      onSaved && onSaved();
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center">
      <div className="animate-slide-up flex max-h-[92vh] w-full max-w-3xl flex-col rounded-t-2xl bg-white sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 p-5">
          <h3 className="font-head text-xl font-bold text-navy">{inv.id ? "Modifica" : "Nuova"} fattura</h3>
          <button data-testid="close-invoice" onClick={onClose}><X className="h-6 w-6 text-slate-400" /></button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-semibold text-navy">Fornitore *</label>
              <input
                data-testid="inv-supplier"
                list="suppliers-list"
                value={inv.supplier_name}
                onChange={(e) => {
                  const s = suppliers.find((x) => x.name === e.target.value);
                  setInv({ ...inv, supplier_name: e.target.value, supplier_id: s?.id || null });
                }}
                className="h-12 w-full rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold"
                placeholder="Nome fornitore"
              />
              <datalist id="suppliers-list">
                {suppliers.map((s) => <option key={s.id} value={s.name} />)}
              </datalist>
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-navy">Data</label>
              <input data-testid="inv-date" type="date" value={inv.date} onChange={(e) => setInv({ ...inv, date: e.target.value })} className="h-12 w-full rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold" />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-semibold text-navy">Valuta</label>
              <select data-testid="inv-currency" value={inv.currency} onChange={(e) => setInv({ ...inv, currency: e.target.value, exchange_rate: e.target.value === "EUR" ? 1 : inv.exchange_rate })} className="h-12 w-full rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold">
                {["EUR", "HRK", "USD", "GBP", "CHF"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-navy">Cambio → EUR</label>
              <input data-testid="inv-rate" type="number" step="0.0001" disabled={inv.currency === "EUR"} value={inv.exchange_rate} onChange={(e) => setInv({ ...inv, exchange_rate: e.target.value })} className="h-12 w-full rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold disabled:bg-slate-100" />
            </div>
            <label className="flex cursor-pointer items-end gap-2 pb-2 text-sm font-semibold text-navy">
              <input data-testid="inv-credit" type="checkbox" checked={inv.is_credit_note} onChange={(e) => setInv({ ...inv, is_credit_note: e.target.checked })} className="h-5 w-5 accent-gold" />
              Nota di credito / reso
            </label>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-sm font-semibold text-navy">Righe prodotto</label>
              <button data-testid="add-line" onClick={addLine} className="flex items-center gap-1 rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-medium hover:bg-slate-200"><Plus className="h-4 w-4" /> Riga</button>
            </div>
            <div className="space-y-2">
              {inv.line_items.map((l, i) => (
                <div key={i} data-testid={`line-${i}`} className="grid grid-cols-12 gap-2 rounded-lg border border-slate-200 p-2">
                  <input placeholder="Prodotto" value={l.product} onChange={(e) => setLine(i, { product: e.target.value })} className="col-span-12 h-10 rounded border border-slate-200 px-2 text-sm outline-none focus:border-gold sm:col-span-4" data-testid={`line-product-${i}`} />
                  <input type="number" placeholder="Qtà" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} className="col-span-3 h-10 rounded border border-slate-200 px-2 text-sm outline-none focus:border-gold sm:col-span-1" />
                  <input placeholder="Un." value={l.unit} onChange={(e) => setLine(i, { unit: e.target.value })} className="col-span-3 h-10 rounded border border-slate-200 px-2 text-sm outline-none focus:border-gold sm:col-span-1" />
                  <select value={l.category_id || ""} onChange={(e) => setLine(i, { category_id: e.target.value })} className="col-span-6 h-10 rounded border border-slate-200 px-2 text-sm outline-none focus:border-gold sm:col-span-3" data-testid={`line-cat-${i}`}>
                    <option value="">Categoria…</option>
                    {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <input type="number" step="0.01" placeholder="Prezzo" value={l.price} onChange={(e) => setLine(i, { price: e.target.value })} className="tnum col-span-9 h-10 rounded border border-slate-200 px-2 text-right text-sm outline-none focus:border-gold sm:col-span-2" data-testid={`line-price-${i}`} />
                  <button onClick={() => removeLine(i)} className="col-span-3 flex items-center justify-center rounded text-red-400 hover:bg-red-50 sm:col-span-1"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <textarea placeholder="Note (opzionale)" value={inv.notes} onChange={(e) => setInv({ ...inv, notes: e.target.value })} className="w-full rounded-lg border-2 border-slate-200 p-3 text-sm outline-none focus:border-gold" rows={2} />
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 p-5">
          <div>
            <div className="text-sm text-slate-500">Subtotale: <span className="tnum font-semibold">{inv.currency} {subtotal.toFixed(2)}</span></div>
            <div className="tnum font-head text-xl font-bold text-navy">Totale: {eur(totalEur)}</div>
          </div>
          <button data-testid="save-invoice-btn" onClick={save} disabled={saving} className="flex h-14 items-center gap-2 rounded-lg bg-gold px-8 font-bold text-black hover:bg-gold-hover disabled:opacity-60">
            {saving && <Loader2 className="h-5 w-5 animate-spin" />} Salva
          </button>
        </div>
      </div>
    </div>
  );
}
